import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { HiChatBubbleLeftRight, HiUser, HiClock, HiTrash } from 'react-icons/hi2';
import { useAuth } from '../../context/AuthContext';
import { useUserRole } from '../../context/UserRoleContext';
import { addComment, getComments, canComment } from '../../services/commercialisationService';
import { firestore } from '../../database/firebase';
import { collection, query, where, orderBy, onSnapshot, deleteDoc, doc } from 'firebase/firestore';

const CommentSection = ({ userId, checklistType = 'readiness' }) => {
  const { currentUser } = useAuth();
  const { userRole } = useUserRole();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const canAddComment = canComment(userRole);

  useEffect(() => {
    if (!userId || !currentUser?.uid) {
      setLoading(false);
      return;
    }

    const commentsRef = collection(
      firestore,
      'commercialisation',
      userId,
      'readinessChecklist',
      userId,
      'comments'
    );

    const q = query(
      commentsRef,
      where('checklistType', '==', checklistType),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      snapshot => {
        const commentsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setComments(commentsData);
        setLoading(false);
      },
      error => {
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId, checklistType, currentUser?.uid]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!newComment.trim() || !canAddComment) return;

    setSubmitting(true);
    try {
      await addComment(userId, checklistType, newComment.trim(), currentUser);
      setNewComment('');
      toast.success('Comment added successfully!');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async commentId => {
    if (!canAddComment || !currentUser?.uid) return;

    try {
      const commentRef = doc(
        firestore,
        'commercialisation',
        userId,
        'readinessChecklist',
        userId,
        'comments',
        commentId
      );
      await deleteDoc(commentRef);
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const formatDate = timestamp => {
    if (!timestamp) return 'Just now';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!canAddComment) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <p className="text-sm text-gray-600">
          Comments are only available for Business Advisors, Coaches and Admins.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <HiChatBubbleLeftRight className="h-5 w-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">Feedback & Comments</h3>
      </div>

      {/* Comment Form */}
      <form onSubmit={handleSubmit} className="mb-6">
        <textarea
          value={newComment}
          onChange={e => setNewComment(e.target.value)}
          placeholder="Add your feedback or comment..."
          rows={4}
          className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
          disabled={submitting}
        />
        <div className="mt-3 flex justify-end">
          <button
            type="submit"
            disabled={!newComment.trim() || submitting}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Sending...' : 'Send Feedback'}
          </button>
        </div>
      </form>

      {/* Comments List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center text-gray-500">
            <div className="mx-auto mb-2 h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
            <p className="text-sm">Loading comments...</p>
          </div>
        ) : comments.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
            <HiChatBubbleLeftRight className="mx-auto mb-2 h-8 w-8 text-gray-400" />
            <p className="text-sm text-gray-600">No comments yet</p>
            <p className="mt-1 text-xs text-gray-500">Be the first to provide feedback</p>
          </div>
        ) : (
          <AnimatePresence>
            {comments.map(comment => (
              <motion.div
                key={comment.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="rounded-lg border border-gray-200 bg-gray-50 p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                      <HiUser className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {comment.commentedByName || comment.commentedByEmail || 'Anonymous'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(comment.createdAt)}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-gray-700">{comment.comment}</p>
                    </div>
                  </div>
                  {comment.commentedBy === currentUser?.uid && (
                    <button
                      onClick={() => handleDelete(comment.id)}
                      className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                    >
                      <HiTrash className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default CommentSection;
